import Hls from 'hls.js';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BuildVersion from '../components/BuildVersion';
import SceneImageDisplay from '../components/SceneImageDisplay';
import SceneInfo from '../components/SceneInfo';
import JourneyProgress from '../components/JourneyProgress';
import { analytics } from '../services/analytics';
import { sceneService } from '../services/sceneService';
import type { Scene } from '../types/scene';
import { performanceMonitor } from '../utils/performance';
import './Player2.css';

interface PlaybackState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  volume: number;
  isLoading: boolean;
  error: string | null;
}

const Player2: React.FC = () => {
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);

  // Web Audio API リアルタイム解析用
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [, setAudioFrequencyData] =
    useState<Uint8Array | null>(null);
  const [, setAudioVolumeLevel] = useState<number>(0); // 音量レベル (0-100)

  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    progress: 0,
    volume: 1,
    isLoading: true,
    error: null,
  });

  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyShown, setSurveyShown] = useState(() => {
    // Check if survey was already shown in this session
    return localStorage.getItem('surveyShown') === 'true';
  });
  const [milestone3MinTracked, setMilestone3MinTracked] = useState(false);
  const [playStartTracked, setPlayStartTracked] = useState(false);

  const [, setSurveyAnswered] = useState(() => {
    // Check if survey was answered (not just skipped)
    const stored = localStorage.getItem('surveyAnswered');
    return stored === 'true';
  });
  const [audioStartTime] = useState<number>(Date.now());

  // Scene management state
  const [currentScene, setCurrentScene] = useState<Scene | null>(null);
  const [sceneDefaults, setSceneDefaults] = useState({
    fadeMs: 800,
    holdMs: 2000,
  });

  // Web Audio API セットアップ
  const setupWebAudio = () => {
    console.log('🚀 setupWebAudio called');
    console.log('audioRef.current:', !!audioRef.current);
    console.log('audioContextRef.current:', !!audioContextRef.current);

    if (!audioRef.current || audioContextRef.current) {
      console.log(
        '❌ Setup skipped - audio ref missing or already initialized'
      );
      return;
    }

    try {
      console.log('🔧 Creating AudioContext...');
      const audioContext = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

      // AudioContextを再開（自動再生ポリシー対応）
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      console.log('📡 Creating analyser...');
      const analyser = audioContext.createAnalyser();
      console.log('🎤 Creating media element source...');
      const source = audioContext.createMediaElementSource(audioRef.current);

      // より詳細な周波数解析のための設定
      analyser.fftSize = 256; // 128個の周波数バンド（80バンドをカバー）
      analyser.smoothingTimeConstant = 0.3; // より反応性を向上
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;

      console.log('🔗 Connecting audio nodes...');
      source.connect(analyser);
      analyser.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      console.log(
        '✅ Web Audio API initialized successfully with enhanced settings'
      );
    } catch (error) {
      console.error('❌ Web Audio API initialization failed:', error);
    }
  };

  // 音量レベル計算（RMS）
  const calculateVolumeLevel = (dataArray: Uint8Array): number => {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += (dataArray[i] / 255.0) ** 2;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    return rms * 100; // 0-100の範囲にスケール
  };

  // リアルタイム音声データ取得
  const updateAudioData = () => {
    if (!analyserRef.current) {
      // Web Audio API が使用できない場合は疑似音量レベルを生成
      const time = Date.now() * 0.002; // ゆっくりした時間
      const baseVolume = 40 + Math.sin(time) * 15; // 25-55の基本音量
      const randomVariation = (Math.random() - 0.5) * 20; // ±10のランダム
      const pseudoVolume = Math.max(
        10,
        Math.min(90, baseVolume + randomVariation)
      );
      setAudioVolumeLevel(pseudoVolume);

      if (playbackState.isPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateAudioData);
      }
      return;
    }

    // 時間ドメインデータ（音量用）と周波数データ（スペクトラム用）を取得
    const bufferLength = analyserRef.current.fftSize;
    const timeDataArray = new Uint8Array(bufferLength);
    const freqDataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    analyserRef.current.getByteTimeDomainData(timeDataArray);
    analyserRef.current.getByteFrequencyData(freqDataArray);

    // 音量レベルを計算
    const volumeLevel = calculateVolumeLevel(timeDataArray);
    setAudioVolumeLevel(volumeLevel);

    // 周波数データが有効かチェック
    const hasValidFreqData = freqDataArray.some(value => value > 0);
    if (hasValidFreqData) {
      setAudioFrequencyData(freqDataArray);
    } else {
      console.log(
        '🔊 Using volume-based visualization (volume:',
        Math.round(volumeLevel),
        ')'
      );
    }

    if (playbackState.isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateAudioData);
    }
  };

  useEffect(() => {
    // Load scene data
    const loadSceneData = async () => {
      try {
        await sceneService.loadSceneData();
        const defaults = sceneService.getSceneDefaults();
        setSceneDefaults(defaults);

        // Set initial scene (time 0)
        const initialScene = sceneService.getCurrentScene(0);
        if (initialScene) {
          setCurrentScene(initialScene);
        }

        console.log('Scene data loaded successfully');
      } catch (error) {
        console.error('Failed to load scene data:', error);
      }
    };

    loadSceneData();

    // Add beforeunload handler to ensure analytics are saved on mobile
    const handleBeforeUnload = () => {
      // Force any pending analytics to be sent immediately
      if ('sendBeacon' in navigator) {
        // Use sendBeacon for reliable data transmission on page unload
        console.log('📡 Page unloading, using sendBeacon for analytics');
      }
    };

    // iOS viewport height fix
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

    if (isIOS) {
      const setViewportHeight = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };

      setViewportHeight();
      window.addEventListener('resize', setViewportHeight);
      window.addEventListener('orientationchange', () => {
        setTimeout(setViewportHeight, 100);
      });

      // Visual Viewport API support (modern iOS)
      if (window.visualViewport) {
        const handleViewportChange = () => {
          const vh = window.visualViewport!.height * 0.01;
          document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        window.visualViewport.addEventListener('resize', handleViewportChange);
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload); // iOS Safari specific

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);

      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const initializeHLS = async () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    const hlsUrl = '/audio/hls/osaka-guide/master.m3u8';

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: false,
        lowLatencyMode: false,
        backBufferLength: 90,

        // Timeout and retry configurations
        manifestLoadingTimeOut: 10000, // 10 seconds for manifest loading
        manifestLoadingMaxRetry: 3, // Retry manifest loading 3 times
        levelLoadingTimeOut: 10000, // 10 seconds for level loading
        levelLoadingMaxRetry: 3, // Retry level loading 3 times
        fragLoadingTimeOut: 20000, // 20 seconds for fragment loading
        fragLoadingMaxRetry: 6, // Retry fragment loading 6 times

        // More tolerant streaming settings
        maxMaxBufferLength: 600, // Max buffer length in seconds
        maxBufferLength: 30, // Initial buffer length in seconds
        maxBufferSize: 60 * 1000 * 1000, // 60MB max buffer size
        maxBufferHole: 0.5, // Max buffer hole tolerance

        // Loading retry delays
        manifestLoadingMaxRetryTimeout: 64000, // Max timeout for manifest retries
        levelLoadingMaxRetryTimeout: 64000, // Max timeout for level retries
        fragLoadingMaxRetryTimeout: 64000, // Max timeout for fragment retries
      });

      hlsRef.current = hls;
      hls.loadSource(hlsUrl);
      hls.attachMedia(audio);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest loaded');
        setPlaybackState(prev => ({ ...prev, isLoading: false }));

        // Track performance metrics
        const audioReadyTime = Date.now();
        performanceMonitor.trackTTFP(audioStartTime, audioReadyTime);
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.error('HLS error:', data);

        // Only show error for fatal errors or after multiple retries
        if (data.fatal) {
          console.error('Fatal HLS error, showing error message');
          analytics.trackError(
            'hls',
            `HLS Fatal Error: ${data.type} - ${data.details}`
          );
          setPlaybackState(prev => ({
            ...prev,
            error: 'Audio loading failed. Please try again.',
            isLoading: false,
          }));
        } else {
          // Non-fatal errors - just log and continue
          console.warn('Non-fatal HLS error, continuing:', data.details);
          analytics.trackError(
            'hls',
            `HLS Warning: ${data.type} - ${data.details}`
          );

          // Try to recover from recoverable errors
          if (data.type === 'mediaError') {
            console.log('Attempting to recover from media error');
            hls.recoverMediaError();
          }
        }
      });
    } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      audio.src = hlsUrl;
      setPlaybackState(prev => ({ ...prev, isLoading: false }));
    } else {
      setPlaybackState(prev => ({
        ...prev,
        error:
          'Your device is not supported. This service requires iOS 14.0+ or Android 8.0+ with compatible browser.',
        isLoading: false,
      }));
    }

    // Audio event listeners
    audio.addEventListener('loadedmetadata', () => {
      setPlaybackState(prev => ({
        ...prev,
        duration: audio.duration,
      }));
    });

    audio.addEventListener('timeupdate', () => {
      const currentTime = audio.currentTime;
      const duration = audio.duration || 1;
      const progress = (currentTime / duration) * 100;

      setPlaybackState(prev => ({
        ...prev,
        currentTime,
        progress,
      }));

      // Update current scene based on time
      const scene = sceneService.getCurrentScene(currentTime);
      if (scene && (!currentScene || scene.time !== currentScene.time)) {
        setCurrentScene(scene);
        console.log('Scene changed to:', scene.title, 'at time:', scene.time);
      }

      // Track 3-minute milestone for all users who reach 180 seconds
      if (currentTime >= 180 && !milestone3MinTracked) {
        analytics.trackAudio3MinMilestone(currentTime); // Detailed logs saved automatically
        setMilestone3MinTracked(true);
      }

      // Show survey after 3 minutes (180 seconds) in production
      if (
        currentTime >= 180 &&
        !surveyShown &&
        !showSurvey &&
        localStorage.getItem('surveyShown') !== 'true'
      ) {
        console.log('Showing survey at', currentTime, 'seconds');

        setShowSurvey(true);
        setSurveyShown(true);
        // Note: Survey display is not tracked in new detailed log system

        // Store in localStorage to prevent showing again in this session
        localStorage.setItem('surveyShown', 'true');
      }
    });

    audio.addEventListener('ended', () => {
      console.log(
        '🎵 [COMPLETION DEBUG] Audio ended event triggered, currentTime:',
        audio.currentTime
      );

      // Analytics: Track completion (detailed logs saved automatically)
      console.log('🎵 [COMPLETION DEBUG] About to track completion reached');
      analytics.trackCompletionReached(audio.currentTime);
      console.log('🎵 [COMPLETION DEBUG] Completion tracking called');

      // Check current state from localStorage to ensure accuracy
      const wasShown = localStorage.getItem('surveyShown') === 'true';
      const wasAnswered = localStorage.getItem('surveyAnswered') === 'true';

      console.log(
        '🎵 Audio ended - surveyShown:',
        wasShown,
        'surveyAnswered:',
        wasAnswered
      );

      // Show survey at end if user didn't answer it during 3-minute mark
      if (
        wasShown &&
        wasAnswered !== true &&
        localStorage.getItem('surveyAnswered') !== 'true'
      ) {
        console.log(
          'Showing end-of-audio survey for user who skipped initial survey'
        );
        setShowSurvey(true);
        // Don't navigate to completion page immediately, let user answer survey first
      } else {
        console.log(
          '✅ Navigating to completion - survey was answered or never shown'
        );
        console.log('🚀 About to call navigate("/completion")');
        // Navigate to completion page if survey was already answered or never shown
        try {
          navigate('/completion');
          console.log('✅ navigate() called successfully');
        } catch {
          console.error('❌ Error calling navigate()');
        }
      }
    });

    audio.addEventListener('play', () => {
      console.log('🎵 Audio play event triggered');
      setPlaybackState(prev => ({ ...prev, isPlaying: true }));

      // Web Audio API 初期化と周波数データ更新開始
      console.log('🔊 Attempting to setup Web Audio API');
      setupWebAudio();
      console.log('📊 Starting audio data updates');
      updateAudioData();

      // Analytics: Track play start (only once, detailed logs saved automatically)
      if (!playStartTracked) {
        const ttfp = Date.now() - audioStartTime;
        analytics.trackAudioPlayStart(ttfp);
        setPlayStartTracked(true);

        // Track network and memory performance (reduced frequency)
        // performanceMonitor.trackNetworkInfo()
        // performanceMonitor.trackMemoryUsage()
      }
    });

    audio.addEventListener('pause', () => {
      setPlaybackState(prev => ({ ...prev, isPlaying: false }));

      // 周波数データ更新を停止
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Note: Pause events are not tracked in the new detailed log system
    });

    // Add error handling for audio element
    audio.addEventListener('error', () => {
      console.error('Audio element error:');
      const audioError = audio.error;
      if (audioError) {
        let errorMessage = 'Audio playback error occurred';
        switch (audioError.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMessage = 'Audio loading was aborted';
            console.log('Audio loading aborted by user');
            return; // Don't show error for user-initiated aborts
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading audio';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Audio decoding error';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio format not supported';
            break;
        }

        console.error('Audio error:', errorMessage, audioError);
        analytics.trackError('general', errorMessage);
        setPlaybackState(prev => ({
          ...prev,
          error: 'Audio loading failed. Please try again.',
          isLoading: false,
        }));
      }
    });

    // Add network state monitoring
    audio.addEventListener('stalled', () => {
      console.warn('Audio loading stalled');
    });

    audio.addEventListener('waiting', () => {
      console.log('Audio buffering...');
    });

    audio.addEventListener('canplay', () => {
      console.log('Audio can play');
    });
  };

  // Separate useEffect for HLS initialization
  useEffect(() => {
    const initialize = async () => {
      await initializeHLS();
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePlay = async () => {
    console.log('🎯 togglePlay called');
    console.log('audioRef.current:', !!audioRef.current);
    console.log('playbackState.isPlaying:', playbackState.isPlaying);

    if (!audioRef.current) {
      console.log('❌ No audio ref available');
      return;
    }

    // ボタン押下アニメーション
    if (playButtonRef.current) {
      playButtonRef.current.classList.add('button-pressed');
      setTimeout(() => {
        playButtonRef.current?.classList.remove('button-pressed');
      }, 300);
    }

    try {
      if (playbackState.isPlaying) {
        console.log('⏸️ Pausing audio...');
        audioRef.current.pause();
      } else {
        console.log('▶️ Playing audio...');
        await audioRef.current.play();
        console.log('✅ Audio play() called successfully');
      }
    } catch (error) {
      console.error('❌ Playback error:', error);
      setPlaybackState(prev => ({
        ...prev,
        error: "We're sorry for the inconvenience. Please try again.",
      }));
    }
  };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;

    const seekTime =
      (parseFloat(event.target.value) / 100) * playbackState.duration;
    audioRef.current.currentTime = seekTime;
  };

  const handleSurveyResponse = async (rating?: string) => {
    console.log('🎯 [MOBILE DEBUG] Survey response:', rating || 'skipped');

    // IMMEDIATELY close the survey popup to prevent user confusion
    setShowSurvey(false);
    setSurveyShown(true);
    localStorage.setItem('surveyShown', 'true');

    console.log('🎯 [MOBILE DEBUG] Survey popup closed immediately');

    // Track analytics (detailed logs saved automatically)
    if (rating) {
      console.log(
        '📝 [MOBILE DEBUG] About to track survey completion:',
        rating
      );

      // For iPhone Safari, use a more aggressive approach with sendBeacon
      if (
        /iPhone.*Safari/.test(navigator.userAgent) &&
        'sendBeacon' in navigator
      ) {
        console.log('📱 [SAFARI DEBUG] Using sendBeacon for reliable tracking');
        try {
          // First try the normal analytics call
          await analytics.trackSurveyCompleted(
            rating as 'excellent' | 'good' | 'fair' | 'poor'
          );
          console.log('✅ [SAFARI DEBUG] Normal analytics call completed');
        } catch {
          console.log('❌ [SAFARI DEBUG] Normal analytics failed');
        }
      } else {
        try {
          await analytics.trackSurveyCompleted(
            rating as 'excellent' | 'good' | 'fair' | 'poor'
          );
          console.log(
            '✅ [MOBILE DEBUG] Survey completion tracked successfully'
          );
        } catch {
          console.log('❌ [MOBILE DEBUG] Failed to track survey completion');
        }
      }

      // Mark as answered (not just skipped)
      setSurveyAnswered(true);
      localStorage.setItem('surveyAnswered', 'true');
      console.log('✅ [MOBILE DEBUG] Survey answered with rating:', rating);
    } else {
      console.log('⏭️ [MOBILE DEBUG] About to track survey skipped');
      try {
        await analytics.trackSurveySkipped();
        console.log('✅ [MOBILE DEBUG] Survey skip tracked successfully');
      } catch {
        console.log('❌ [MOBILE DEBUG] Failed to track survey skip');
      }
      // Survey skipped - no detailed log entry needed
      setSurveyAnswered(false);
      localStorage.setItem('surveyAnswered', 'false');
      console.log(
        '⏭️ [MOBILE DEBUG] Survey skipped - Continue Listening pressed'
      );
    }

    // If this is the end-of-audio survey, navigate to completion after response
    if (audioRef.current?.ended) {
      console.log(
        '📱 End-of-audio survey completed, waiting for analytics then navigating'
      );

      const isIPhoneSafari = /iPhone.*Safari/.test(navigator.userAgent);

      if (isIPhoneSafari) {
        // Method 1: Store in multiple localStorage keys as backup
        const backupData = {
          type: 'end_survey_completed',
          rating: rating,
          sessionId: analytics.getSessionId(),
          timestamp: new Date().toISOString(),
          completed: true,
        };
        localStorage.setItem(
          'hep5_backup_end_survey',
          JSON.stringify(backupData)
        );
        localStorage.setItem(
          `hep5_survey_${analytics.getSessionId()}`,
          rating || ''
        );
        console.log('🍎 [SAFARI FIX] Backup data stored in localStorage');

        // Dramatically increase wait time and add multiple confirmation checks
        let confirmationAttempts = 0;
        const maxConfirmationAttempts = 5;

        const checkAndNavigate = () => {
          confirmationAttempts++;
          console.log(
            `🍎 [SAFARI FIX] Confirmation attempt ${confirmationAttempts}/${maxConfirmationAttempts}`
          );

          // Check if we have confirmation that data was saved
          const surveyAnsweredStatus = localStorage.getItem('surveyAnswered');
          console.log(
            `🍎 [SAFARI FIX] Current surveyAnswered status: ${surveyAnsweredStatus}`
          );

          if (
            surveyAnsweredStatus === 'true' ||
            confirmationAttempts >= maxConfirmationAttempts
          ) {
            console.log('🍎 [SAFARI FIX] Proceeding with navigation');
            navigate('/completion');
          } else {
            console.log(
              `🍎 [SAFARI FIX] Waiting longer, attempt ${confirmationAttempts + 1} in 1000ms`
            );
            setTimeout(checkAndNavigate, 500);
          }
        };

        // Start with longer initial delay
        setTimeout(checkAndNavigate, 500);
      } else {
        // Non-iPhone Safari: use normal timing
        setTimeout(() => {
          console.log('🚀 Navigating to completion page');
          navigate('/completion');
        }, 1500);
      }
    }

    console.log(
      'Survey state updated - surveyShown: true, surveyAnswered:',
      !!rating,
      'showSurvey: false'
    );
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (playbackState.error) {
    return (
      <div className="player-page error-state">
        <div className="error-content">
          <h2>Playback Error</h2>
          <p>{playbackState.error}</p>
          <button onClick={() => navigate('/')} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="player-page">
        {/* Audio Element */}
        <audio ref={audioRef} preload="none" style={{ display: 'none' }} />

        {/* Main Content */}
        <div className="player-interface">
          {/* 1. Header with title and headphones */}
          <div className="player-header">
            <h1 className="player-title">Discover Osaka from the Sky</h1>
            <div className="headphone-reminder">
              <span className="headphone-icon">🎧</span>
            </div>
          </div>

          {/* 2. Scene Image Display */}
          <div className="scene-image-section">
            <SceneImageDisplay
              scene={currentScene}
              fadeMs={sceneDefaults.fadeMs}
              holdMs={sceneDefaults.holdMs}
            />
          </div>

          {/* 3. Scene Title and Description */}
          <SceneInfo
            scene={currentScene}
            currentTime={playbackState.currentTime}
            className="scene-info-display main-scene-info"
          />

          {/* Journey Progress */}
          <JourneyProgress 
            currentTime={playbackState.currentTime}
            className="scene-journey-progress"
          />

          {/* Credits Section */}
          <div className="credits-section">
            {/* Re:Sense Technology Credit */}
            <div className="tech-credit">
              <span className="credit-label">Powered by</span>
              <img
                src="/resense-streaming-logo.png"
                alt="Re:Sense STREAMING"
                className="resense-logo-completion"
              />
            </div>

            {/* Company Attribution */}
            <div className="company-credit">
              <span className="operated-by">Operated by</span>
              <span className="company-name">Clepseadra, Inc.</span>
            </div>

            {/* Copyright */}
            <div className="copyright">
              <span>© 2025 HEP FIVE OSAKA UMEDA</span>
            </div>
          </div>
        </div>

        {/* Fixed Player Controls at Bottom */}
        <div className="fixed-player-controls">
          {/* Row 1: Seek Bar */}
          <div className="progress-container">
            {/* シーンマーカー */}
            <div className="scene-markers">
              {sceneService.getAllScenes().map(scene => {
                const markerPosition =
                  playbackState.duration > 0
                    ? (scene.time / playbackState.duration) * 100
                    : 0;
                return (
                  <div
                    key={scene.time}
                    className={`scene-marker ${playbackState.currentTime >= scene.time ? 'passed' : ''}`}
                    style={{
                      left: `calc(${markerPosition}% + 6px)`,
                    }}
                    title={scene.title}
                  />
                );
              })}
            </div>

            <input
              type="range"
              min="0"
              max="100"
              value={playbackState.progress}
              onChange={handleSeek}
              className="progress-bar"
              disabled={playbackState.isLoading}
            />
          </div>

          {/* Row 2: Time Display and Play Button */}
          <div className="controls-row">
            <span className="current-time">
              {formatTime(playbackState.currentTime)}
            </span>

            <button
              ref={playButtonRef}
              className={`play-button ${playbackState.isPlaying ? 'playing' : ''}`}
              onClick={togglePlay}
              disabled={playbackState.isLoading}
            >
              {playbackState.isLoading ? (
                <span className="loading-spinner">⏳</span>
              ) : playbackState.isPlaying ? (
                <img src="/pause-icon.svg" alt="Pause" width="32" height="32" />
              ) : (
                <img src="/play-icon.svg" alt="Play" width="32" height="32" />
              )}
            </button>

            <span className="total-time">
              {formatTime(playbackState.duration)}
            </span>
          </div>
        </div>
      </div>

      {/* Survey Modal */}
      {showSurvey && (
        <div
          className="survey-modal"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleSurveyResponse();
          }}
        >
          <div className="survey-content" onClick={e => e.stopPropagation()}>
            <h3>
              {audioRef.current?.ended
                ? 'Thank you for listening! How was your experience?'
                : 'How was your experience?'}
            </h3>
            <p>Help us improve the audio guide</p>
            <div className="survey-buttons">
              <button
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSurveyResponse('excellent');
                }}
                className="survey-btn excellent"
              >
                Excellent
              </button>
              <button
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSurveyResponse('good');
                }}
                className="survey-btn good"
              >
                Good
              </button>
              <button
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSurveyResponse('fair');
                }}
                className="survey-btn fair"
              >
                Fair
              </button>
              <button
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSurveyResponse('poor');
                }}
                className="survey-btn poor"
              >
                Poor
              </button>
            </div>
            <button
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleSurveyResponse();
              }}
              className="survey-skip"
            >
              Continue Listening
            </button>
          </div>
        </div>
      )}

      {/* Build Version Display */}
      <BuildVersion />
    </>
  );
};

export default Player2;
